import React, { useEffect, useMemo, useState } from 'react';
import { Activity, ShieldAlert } from 'lucide-react';
import { apiClient } from '../../api/client';
import { logger } from '../../lib/logger';
import { useNow } from '../../lib/useCoinData';
import { ageSeconds, ageText } from './DataFreshness';

/** Header badge: scan age + complete-data count + backend health (VERİ BAYAT when stale / OKX errors). */
export default function DataHealthBadge({ rows, streamStatus }) {
    const now = useNow(1000);
    const [health, setHealth] = useState(null);
    useEffect(() => {
        let cancelled = false;
        const poll = async () => {
            try {
                const { data } = await apiClient.get('/system/health');
                if (!cancelled) setHealth(data);
            } catch (e) {
                logger.warn('system health poll failed', e);
            }
        };
        poll();
        const id = setInterval(poll, 15000);
        return () => { cancelled = true; clearInterval(id); };
    }, []);

    const { latest, total, incomplete } = useMemo(() => {
        const list = rows || [];
        const ts = list.map((r) => (r.analyzed_at ? new Date(r.analyzed_at).getTime() : 0));
        return {
            latest: ts.length ? new Date(Math.max(...ts)).toISOString() : null,
            total: list.length,
            incomplete: list.filter((r) => r.data_quality && !r.data_quality.complete),
        };
    }, [rows]);
    const age = ageSeconds(latest, now);
    const live = streamStatus === 'open' || streamStatus === 'connected';
    const degraded = health?.status === 'degraded';
    const warming = health?.status === 'starting';
    const bad = age === null || age > 90 || incomplete.length > 0;
    const tip = degraded
        ? `VERİ BAYAT — sinyallere güvenme: ${(health.reasons_tr || []).join(' · ')}`
        : incomplete.length
            ? `Eksik veri: ${incomplete.map((r) => `${r.symbol.replace('USDT', '')} (${r.data_quality.missing.join(', ')})`).join(' · ')}`
            : 'Taranan tüm coinlerde mum, fonlama, L/S ve OI verisi tam.';
    const tone = degraded
        ? 'border-rose-500/50 bg-rose-500/15 text-rose-200'
        : bad ? 'border-amber-500/40 bg-amber-500/10 text-amber-200' : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200';
    return (
        <div
            data-testid="data-health-badge"
            data-health-status={degraded ? 'degraded' : 'ok'}
            title={tip}
            className={`inline-flex items-center gap-2 text-[10px] font-mono border rounded-md px-2 py-1 max-w-full flex-wrap ${tone}`}
        >
            {degraded ? <ShieldAlert className="h-3.5 w-3.5" /> : <Activity className={`h-3.5 w-3.5 ${live ? 'animate-pulse' : ''}`} />}
            <span>{degraded ? 'VERİ BAYAT' : warming ? 'ISINIYOR' : live ? 'CANLI' : 'BAĞLANIYOR'}</span>
            <span className="text-slate-400">·</span>
            <span data-testid="data-health-age">tarama {ageText(age)}</span>
            <span className="text-slate-400">·</span>
            <span data-testid="data-health-complete">{total ? `${total - incomplete.length}/${total} coin tam veri` : 'veriler yükleniyor…'}</span>
            {degraded && (
                <span data-testid="data-health-degraded" className="basis-full text-[10px] font-sans text-rose-100">
                    ⚠ sinyallere güvenme — {health.reasons_tr?.[0]}
                </span>
            )}
        </div>
    );
}
